import { useId } from 'react';
import { cn } from '@/lib/utils';

/** The product name, in one place, so renaming it is one edit and not thirty. */
export const PRODUCT_NAME = 'DovoCRM';

/**
 * The DovoCRM mark: a coral tile holding a geometric lowercase "d" whose bowl
 * is a conversation and whose stem is the pipeline it feeds, with the small
 * spark of a new lead beside it. Drawn inline so it is crisp at any size and
 * follows the page's colours.
 */
export function BrandMark({ size = 32, className }: { size?: number; className?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={cn('shrink-0', className)}
      role="img"
      aria-label={PRODUCT_NAME}
    >
      <defs>
        <linearGradient id={`dovo-g-${id}`} x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FF7E5F" />
          <stop offset="1" stopColor="#E9483A" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="17" fill={`url(#dovo-g-${id})`} />
      <circle cx="28" cy="37" r="10.5" fill="none" stroke="#fff" strokeWidth="7" />
      <path d="M38.5 15v32.5" stroke="#fff" strokeWidth="7" strokeLinecap="round" />
      <circle cx="19" cy="18.5" r="4" fill="#fff" opacity="0.92" />
    </svg>
  );
}

/**
 * The logo: the mark beside the "dovo crm" wordmark.
 *
 * `tone="dark"` is for slate surfaces (white wordmark); `showTagline={false}`
 * shows the mark alone, for tight spaces.
 */
export function Brand({
  tone = 'light',
  size = 'md',
  showTagline = true,
  className,
}: {
  tone?: 'light' | 'dark';
  size?: 'sm' | 'md';
  showTagline?: boolean;
  className?: string;
}) {
  const mark = size === 'sm' ? 30 : 38;
  if (!showTagline) return <BrandMark size={mark} className={className} />;
  return (
    <span className={cn('inline-flex select-none items-center gap-2.5', className)} dir="ltr">
      <BrandMark size={mark} />
      <span
        className={cn(
          'font-extrabold leading-none tracking-[-0.04em]',
          size === 'sm' ? 'text-[1.35rem]' : 'text-[1.7rem]',
          tone === 'dark' ? 'text-white' : 'text-slate-900 dark:text-white',
        )}
      >
        dovo<span className="font-semibold text-primary">crm</span>
      </span>
    </span>
  );
}
